(() => {
  if (window.__lachikaHeroInitialized) return;
  window.__lachikaHeroInitialized = true;
  const instances = new WeakMap();

  class LachikaHero {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('.lachika-hero__slide')];
      this.dots = [...root.querySelectorAll('[data-hero-dot]')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.speed = Math.max(4000, Number(root.dataset.speed) || 6000);
      this.autoRotate = root.dataset.autoRotate === 'true' && this.slides.length > 1;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pausedByUser = false;
      this.visible = true;
      this.timer = null;
      this.abort = new AbortController();
      const { signal } = this.abort;

      root.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.show(this.index - 1), { signal });
      root.querySelector('[data-hero-next]')?.addEventListener('click', () => this.show(this.index + 1), { signal });
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index), { signal }));
      root.addEventListener('mouseenter', () => this.pause(), { signal });
      root.addEventListener('mouseleave', () => this.start(), { signal });
      root.addEventListener('focusin', () => this.pause(), { signal });
      root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) this.start();
      }, { signal });
      root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
        if (event.key === 'ArrowRight') this.show(this.index + 1);
      }, { signal });
      document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
      this.reducedMotion.addEventListener('change', () => this.syncPlayback(), { signal });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.syncPlayback();
        }, { threshold: 0.15 });
        this.observer.observe(root);
      }
      this.update();
      this.syncPlayback();
    }

    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.update();
      this.syncPlayback();
    }

    update() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        const video = slide.querySelector('video');
        if (video && !active) video.pause();
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    }

    syncPlayback() {
      const canPlay = this.visible && !document.hidden && !this.pausedByUser;
      const activeVideo = this.slides[this.index]?.querySelector('video');
      if (activeVideo) {
        if (canPlay && !this.reducedMotion.matches) activeVideo.play().catch(() => {});
        else activeVideo.pause();
      }
      this.stopTimer();
      if (this.autoRotate && canPlay && !this.reducedMotion.matches) {
        this.timer = window.setInterval(() => this.show(this.index + 1), this.speed);
      }
    }

    pause() {
      this.pausedByUser = true;
      this.syncPlayback();
    }

    start() {
      this.pausedByUser = false;
      this.syncPlayback();
    }

    stopTimer() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    destroy() {
      this.stopTimer();
      this.slides.forEach((slide) => slide.querySelector('video')?.pause());
      this.observer?.disconnect();
      this.abort.abort();
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.('.lachika-hero') ? [scope] : scope.querySelectorAll('.lachika-hero');
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new LachikaHero(root));
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.lachika-hero').forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('.lachika-hero__slide');
    if (!slide) return;
    const root = slide.closest('.lachika-hero');
    const instance = instances.get(root);
    instance?.show(instance.slides.indexOf(slide));
    instance?.pause();
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('.lachika-hero');
    if (root) instances.get(root)?.start();
  });
})();
